/**
 * Faction icons.
 *
 * One place that knows where the artwork lives, because it is needed in three
 * very different contexts: as an HTML image on the reputation board, as a
 * texture painted onto a station deck, and as a billboard on the map. Letting
 * each of those build its own path is how one of them ends up quietly broken
 * after a rename.
 *
 * (The turquoise file was delivered as `tourquoise.png` and renamed on the way
 * in, so the filename matches the `FactionId` used everywhere in code.)
 */

import type { FactionId } from '@interspacial/shared';
import * as THREE from 'three';

/** Served from `apps/client/public/factions`, under the app's base path. */
export function factionIconUrl(faction: FactionId): string {
  return `${import.meta.env.BASE_URL}factions/${faction}.png`;
}

const textures = new Map<FactionId, THREE.Texture>();

/**
 * The icon as a texture, shared by every station flying that flag.
 *
 * Cached and never disposed: there are six of them, they are needed for as long
 * as anything is on screen, and re-decoding a megapixel PNG per station would
 * be pure waste.
 */
export function factionTexture(faction: FactionId): THREE.Texture {
  const existing = textures.get(faction);
  if (existing) return existing;

  const texture = new THREE.TextureLoader().load(factionIconUrl(faction));
  texture.colorSpace = THREE.SRGBColorSpace;
  // Icons are read at a glance and often at a steep angle across a deck, so
  // anisotropy earns its keep here more than sharpness at normal incidence.
  texture.anisotropy = 4;

  textures.set(faction, texture);
  return texture;
}
